'use client';

import { Input } from '@/components/Input';
import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/calendar';
import { CalendarDays, Info } from 'lucide-react';
import { formatDate } from '@/utility/utility';
import {
  formatTypedDate,
  parseTypedDate,
  typedDateBoundsError,
  typedDatePattern,
  type TypedDateError,
} from '@/utility/typedDate';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover';
import { dateMatchModifiers, type Matcher } from 'react-day-picker';
import styles from './DatePicker.module.css';
import { useDateBounds } from './useDateBounds';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { fr, enUS, type Locale } from 'date-fns/locale';

export interface DatePickerProps {
  id?: string;
  className?: string;
  date: Date | undefined;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
  startMonth?: Date;
  endMonth?: Date;
  placeholder?: string;
  disabledDates?: Matcher[];
  disabled?: boolean;
  onBlur?: () => void;
  onChange: (date: Date | undefined) => void;
  error?: string;
  locale?: string;
  /**
   * Reports a typed date the field refused (unreadable, outside the min/max,
   * or a disabled day), and `undefined` once it is fixed. Passing it means
   * the host shows the message (a Formik field shows it under its label);
   * without it, the picker shows the message under the input itself.
   */
  onInvalidChange?: (message: string | undefined) => void;
}

const dateFnsLocaleMap: Record<string, Locale> = {
  fr,
  en: enUS,
};

const sameDay = (a: Date | undefined, b: Date | undefined) =>
  !!a && !!b && a.toDateString() === b.toDateString();

/**
 * A date field that takes a typed date or a pick from the calendar.
 *
 * Typing: the field shows the date as text (`12 oct. 2026`); on focus it
 * switches to the numeric form the user types (`12/10/2026` in French,
 * `10/12/2026` in English, ISO in both), and the typed text is read on blur
 * or Enter. An unreadable date, one outside `minDate`/`maxDate`, or a
 * disabled day is refused with a message and the previous value is kept.
 *
 * Calendar: clicking the field (or the calendar icon, or ArrowDown) opens
 * it. It is non-modal so the input keeps focus while it is open, and the
 * calendar follows what is being typed.
 */
export function DatePicker({
  id,
  date,
  onBlur,
  onChange,
  className,
  placeholder,
  startMonth = new Date('1950-01-01'),
  endMonth = new Date('2050-12-31'),
  minDate,
  maxDate,
  disabledDates,
  disabled = false,
  error,
  locale,
  onInvalidChange,
}: DatePickerProps) {
  const { t } = useTranslation(TranslationNS.Filters);
  const formatHint = t('date_picker.format_hint', {
    defaultValue: typedDatePattern(locale).toUpperCase(),
  });
  const effectivePlaceholder = placeholder || formatHint;
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(date);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() =>
    formatDate(date, { format: 'medium', locale })
  );
  const [typedError, setTypedError] = useState<TypedDateError | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Typed text not yet read (cleared on commit or a calendar pick).
  const dirtyRef = useRef(false);
  // Open the calendar with focus on its days (icon, ArrowDown) rather than
  // leaving it in the input (a click on the field).
  const focusCalendarRef = useRef(false);
  const dateRef = useRef(date);
  dateRef.current = date;

  const dateKey = date?.getTime();
  const displayText = (value: Date | undefined) =>
    value ? formatDate(value, { format: 'medium', locale }) : '';

  // Follow the value the host holds, unless the user is typing over it.
  useEffect(() => {
    if (focused) return;
    if (!typedError) setText(displayText(dateRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateKey, locale]);

  // A new value from the host (a reset, another field) clears a refusal.
  const previousKey = useRef(dateKey);
  useEffect(() => {
    if (previousKey.current === dateKey) return;
    previousKey.current = dateKey;
    if (typedError) reportError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateKey]);

  const {
    effectiveStartMonth,
    effectiveEndMonth,
    renderStartMonth,
    renderEndMonth,
    lowerBound,
    upperBound,
    computedDisabledDates,
  } = useDateBounds({ startMonth, endMonth, minDate, maxDate, disabledDates });
  const errorMessage = (kind: TypedDateError) => {
    const bound =
      kind === 'before_min'
        ? lowerBound
        : kind === 'after_max'
          ? upperBound
          : undefined;
    return t(`date_picker.errors.${kind}`, {
      format: formatHint,
      ...(bound && { date: formatDate(bound, { format: 'medium', locale }) }),
    });
  };

  function reportError(kind: TypedDateError | null) {
    setTypedError(kind);
    onInvalidChange?.(kind ? errorMessage(kind) : undefined);
  }

  /**
   * Reads the typed text. Returns the date the field now holds, or null when
   * the text was refused (the text stays so it can be corrected).
   */
  const commitText = (): Date | undefined | null => {
    if (!dirtyRef.current) return dateRef.current;
    dirtyRef.current = false;

    if (!text.trim()) {
      reportError(null);
      if (dateRef.current) onChange(undefined);
      setMonth(undefined);
      return undefined;
    }

    const parsed = parseTypedDate(text, locale);
    if (!parsed) {
      reportError('invalid');
      return null;
    }
    const boundsError = typedDateBoundsError(parsed, {
      minDate: lowerBound,
      maxDate: upperBound,
      isUnavailable: disabledDates?.length
        ? (day) => dateMatchModifiers(day, disabledDates)
        : undefined,
    });
    if (boundsError) {
      reportError(boundsError);
      return null;
    }

    reportError(null);
    setMonth(parsed);
    if (!sameDay(parsed, dateRef.current)) onChange(parsed);
    return parsed;
  };

  // ── Typing ──
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    if (!typedError) {
      setText(date ? formatTypedDate(date, locale) : '');
    }
    const input = e.target;
    requestAnimationFrame(() => input.select());
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dirtyRef.current = true;
    setText(e.target.value);
    // Let the calendar follow a readable date while it is typed.
    const parsed = parseTypedDate(e.target.value, locale);
    if (parsed) setMonth(parsed);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Focus moving into the calendar is not leaving the field.
    if (contentRef.current?.contains(e.relatedTarget as Node | null)) return;
    setFocused(false);
    const result = commitText();
    if (result !== null) setText(displayText(result));
    if (!open) onBlur?.();
  };

  const openCalendar = (withFocus: boolean) => {
    if (disabled) return;
    focusCalendarRef.current = withFocus;
    setMonth(dateRef.current ?? month);
    setOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      commitText();
      openCalendar(true);
    } else if (e.key === 'Enter' && dirtyRef.current) {
      // Read the typed date instead of submitting the form with the old one.
      e.preventDefault();
      const result = commitText();
      if (result) setText(formatTypedDate(result, locale));
      setOpen(false);
    }
  };

  // ── Select Date ──
  const pick = (selected: Date | undefined) => {
    dirtyRef.current = false;
    reportError(null);
    setMonth(selected);
    const inputFocused = document.activeElement === inputRef.current;
    setText(
      selected
        ? inputFocused
          ? formatTypedDate(selected, locale)
          : displayText(selected)
        : ''
    );
    onChange?.(selected);
    setOpen(false);
  };

  const handleCalendarDateSelect = (selectedDate?: Date) => {
    if (selectedDate && dateMatchModifiers(selectedDate, computedDisabledDates))
      return;
    pick(selectedDate);
  };

  // ── Month Change ──
  const handleMonthChange = (newMonth: Date) => {
    const y = newMonth.getFullYear();
    const m = newMonth.getMonth();

    if (effectiveStartMonth) {
      const sy = effectiveStartMonth.getFullYear();
      const sm = effectiveStartMonth.getMonth();
      if (y < sy || (y === sy && m < sm)) {
        setMonth(effectiveStartMonth);
        return;
      }
    }

    if (effectiveEndMonth) {
      const ey = effectiveEndMonth.getFullYear();
      const em = effectiveEndMonth.getMonth();
      if (y > ey || (y === ey && m > em)) {
        setMonth(effectiveEndMonth);
        return;
      }
    }

    setMonth(newMonth);
  };

  const handleClear = () => pick(undefined);

  const handleToday = () => {
    const today = new Date();
    if (dateMatchModifiers(today, computedDisabledDates)) return;
    pick(today);
  };

  const onOpenChange = (isOpen: boolean) => {
    if (disabled) return;
    setOpen(isOpen);
    if (!isOpen && document.activeElement !== inputRef.current) onBlur?.();
  };

  const showOwnMessage = !onInvalidChange && !!typedError;
  const messageId = id ? `${id}-date-error` : undefined;
  const hasError = !!error || !!typedError;

  return (
    <Popover
      open={disabled ? false : open}
      modal={false}
      onOpenChange={onOpenChange}
    >
      <div className={styles.datepickerRoot}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className={styles.datepickerAnchor}>
            <Input
              ref={inputRef}
              id={id}
              disabled={disabled}
              autoComplete="off"
              value={text}
              placeholder={focused ? formatHint : effectivePlaceholder}
              aria-invalid={hasError || undefined}
              aria-describedby={showOwnMessage ? messageId : undefined}
              onFocus={handleFocus}
              onChange={handleTextChange}
              onBlur={handleInputBlur}
              onClick={() => !open && openCalendar(false)}
              onKeyDown={handleKeyDown}
              className={cn(
                styles.datepickerInput,
                hasError && styles.datepickerError,
                className
              )}
            />
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              aria-label={t('date_picker.open_calendar')}
              className={styles.datepickerToggle}
              // Keep focus (and any typed text) where it is.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => (open ? setOpen(false) : openCalendar(true))}
            >
              {hasError ? (
                <Info className={styles.datepickerToggleIcon} />
              ) : (
                <CalendarDays className={styles.datepickerToggleIcon} />
              )}
            </button>
          </div>
        </PopoverAnchor>
        {showOwnMessage && typedError && (
          <p id={messageId} role="alert" className={styles.datepickerMessage}>
            {errorMessage(typedError)}
          </p>
        )}
      </div>

      <PopoverContent
        ref={contentRef}
        align="start"
        sideOffset={4}
        avoidCollisions={true}
        collisionPadding={12}
        className={styles.datepickerPopover}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (!focusCalendarRef.current) return;
          requestAnimationFrame(() => {
            const day =
              contentRef.current?.querySelector<HTMLButtonElement>(
                'button[data-day][tabindex="0"]'
              ) ??
              contentRef.current?.querySelector<HTMLButtonElement>(
                'button[data-day]:not([disabled])'
              );
            day?.focus();
          });
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (contentRef.current?.contains(document.activeElement)) {
            inputRef.current?.focus();
          }
        }}
        onInteractOutside={(e) => {
          // A click in the field keeps the calendar open while typing.
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        onMouseDown={(e) => {
          // Clicking a day or a footer button must not blur the input (that
          // would read a half-typed date). The month/year selects need it.
          if (!(e.target instanceof HTMLSelectElement)) e.preventDefault();
        }}
      >
        <Calendar
          mode="single"
          selected={date}
          month={month}
          onMonthChange={handleMonthChange}
          captionLayout="dropdown"
          startMonth={renderStartMonth}
          endMonth={renderEndMonth}
          disabled={computedDisabledDates}
          onSelect={handleCalendarDateSelect}
          locale={dateFnsLocaleMap[locale ?? 'en'] ?? enUS}
          formatters={{
            formatMonthDropdown: (date) =>
              date.toLocaleString(locale ?? 'en', { month: 'long' }),
          }}
        />

        <div className={styles.datepickerFooter}>
          <button
            type="button"
            className={styles.datepickerBtnToday}
            onClick={handleToday}
          >
            {t('date_picker.today')}
          </button>

          <div className={styles.datepickerFooterActions}>
            <button
              type="button"
              className={styles.datepickerBtnClear}
              onClick={handleClear}
            >
              {t('date_picker.clear')}
            </button>
            <button
              type="button"
              className={styles.datepickerBtnApply}
              onClick={() => setOpen(false)}
            >
              {t('date_picker.apply')}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
